import { getProductsStationSuggestions } from '@/api/generated/products-stations/products-stations';
import type { ListFilter, ListRequest, StationSuggestion } from '@/api/generated/schemas';
import {
  defineEntityAsyncSelector,
  listRequestQueryKey,
  selectorPresets,
  toListRequest,
  type BoundSelectorProps,
  type NoPickFilters,
} from '@/shared/ui/async-selector';
import { createAsyncSelectorField, type AsyncSelectorFieldProps } from '@/shared/forms';
import type { StationRef } from '../productsApi';

/**
 * Typové doménové filtry selektoru stanic — místo syrového `ListFilter[]`, takže call-site nemůže udělat
 * překlep v názvu pole ani hodnotě a refactoring je bezpečný. Mapování na list protokol řeší
 * {@link toStationExtraFilters}; BE podpora je `StationSuggestionsSpec.BoolFilter("domestic")`.
 */
export interface StationFilters {
  /** `false` = jen cílové stanice (v destinaci), `true` = jen tuzemské (nástupní); neuvedeno = bez omezení. */
  domestic?: boolean;
}

function toStationExtraFilters(filters: StationFilters | undefined): ListFilter[] | undefined {
  if (!filters) return undefined;
  const out: ListFilter[] = [];
  if (filters.domestic !== undefined) {
    out.push({ field: 'domestic', text: String(filters.domestic) });
  }
  return out.length > 0 ? out : undefined;
}

const stationSelector = defineEntityAsyncSelector<
  StationRef,
  StationSuggestion,
  ListRequest,
  NoPickFilters
>({
  api: {
    fetch: getProductsStationSuggestions,
    buildApiParams: (ctx) => toListRequest(ctx.search, ctx.ids, ctx.extraFilters ?? []),
    queryKey: (request) => listRequestQueryKey('products.stations.suggestions', request),
    mapItem: (s) => ({
      stationId: s.id,
      name: s.name ?? '',
      code: s.code ?? null,
      isDomestic: s.isDomestic,
      latitude: s.latitude ?? null,
      longitude: s.longitude ?? null,
    }),
  },
  key: (item) => item.stationId,
  objectCategory: 'Station',
  // Tři tečky (⋯) otevřou list page ObjectCategory v Magic desktopu (jako ostatní selektory).
  listPicker: 'legacy',
  display: {
    kind: 'nameCode',
    name: (item) => item.name,
    code: (item) => item.code,
  },
  columns: (t) =>
    selectorPresets.nameAndCode({
      name: {
        id: 'name',
        header: t('stationChainSelectors.station.columns.name'),
        value: (item) => item.name,
        sortable: true,
      },
      code: {
        id: 'code',
        header: t('stationChainSelectors.station.columns.code'),
        value: (item) => item.code,
        emptyDash: '—',
      },
    }),
  i18n: { namespace: 'products' },
});

const BaseStationSelector = stationSelector.Selector;
const BaseStationSelectorField = createAsyncSelectorField(BaseStationSelector);

export type StationSelectorProps = Omit<BoundSelectorProps<StationRef>, 'extraFilters'> & {
  /** Typové doménové filtry (viz {@link StationFilters}). */
  extraFilters?: StationFilters;
};

/** Selektor stanic ze sdíleného číselníku; `extraFilters` je typové (viz {@link StationFilters}). */
export function StationSelector({ extraFilters, ...props }: StationSelectorProps) {
  return <BaseStationSelector {...props} extraFilters={toStationExtraFilters(extraFilters)} />;
}

export type StationSelectorFieldProps = Omit<AsyncSelectorFieldProps, 'extraFilters'> & {
  /** Typové doménové filtry (viz {@link StationFilters}). */
  extraFilters?: StationFilters;
};

/** Pole stanice pro TanStack Form (single/multi); `extraFilters` je typové (viz {@link StationFilters}). */
export function StationSelectorField({ extraFilters, ...props }: StationSelectorFieldProps) {
  return <BaseStationSelectorField {...props} extraFilters={toStationExtraFilters(extraFilters)} />;
}
